import React from 'react';
import styled from 'styled-components';
import { FaRegClock, FaEye } from 'react-icons/fa';
import { Icon } from '../common/Icon';
import { config } from '../../config';
import { Link } from 'react-router-dom';

// Interface for meditation result data
export interface MeditationResultItem {
  id: string;
  art_file: string | null;
  script_name: string;
  script_description: string;
  is_unguided: boolean;
  final_duration: number | null; // Duration in seconds
  views: number;
  published_at: string;
  username: string;
  profile_image: string | null;
  voice_name?: string | null; // Voice name for guided meditations
}

interface MeditationResultsProps {
  meditations: MeditationResultItem[];
  loading?: boolean;
  onMeditationClick?: (meditation: MeditationResultItem) => void;
}

// Styled components
const Container = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(360px, 1fr));
  gap: 24px;
  padding: 20px 0;
`;

const MeditationCard = styled.a<{ $backgroundImage?: string }>`
  position: relative;
  height: 280px;
  border-radius: 12px;
  overflow: hidden;
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  background: ${props => props.$backgroundImage 
    ? `linear-gradient(rgba(0, 0, 0, 0.3), rgba(0, 0, 0, 0.6)), url(${props.$backgroundImage})`
    : 'linear-gradient(135deg, #2c3e50, #3498db)'
  };
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  display: block;
  text-decoration: none;
  color: inherit;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.3);
    text-decoration: none;
    color: inherit;
  }

  &:visited {
    color: inherit;
  }
`;

const CardContent = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 20px;
  background: linear-gradient(transparent, rgba(0, 0, 0, 0.8));
  color: white;
`;

const MeditationTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  margin: 0 0 8px 0;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.8);
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const MeditationDescription = styled.p`
  font-size: 14px;
  line-height: 1.4;
  margin: 0 0 12px 0;
  text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.8);
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  opacity: 0.9;
`;

const CardMeta = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
`;

const AuthorInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ProfileImage = styled.img`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  border: 1px solid rgba(255, 255, 255, 0.4);
`;

const ProfileLink = styled(Link)`
  display: flex;
  align-items: center;
  text-decoration: none;
  color: inherit;
  transition: opacity 0.2s ease;
  
  &:hover {
    opacity: 0.8;
  }
`;

const AuthorName = styled.span`
  font-size: 13px;
  font-weight: 500;
  text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.8);
`;

const MeditationStats = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 12px;
  text-shadow: 1px 1px 2px rgba(0, 0, 0, 0.8);
`;

const StatItem = styled.span`
  display: flex;
  align-items: center;
  gap: 4px;
  opacity: 0.9;
  
  svg {
    font-size: 12px;
  }
`;

const TypeBadgeContainer = styled.div`
  position: absolute;
  top: 12px;
  right: 12px;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 4px;
`;

const TypeBadge = styled.div<{ $isUnguided: boolean }>`
  background: ${props => props.$isUnguided ? '#9c27b0' : '#4caf50'};
  color: white;
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
`;

const VoiceName = styled.div`
  background: rgba(76, 175, 80, 0.9);
  color: white;
  padding: 2px 6px;
  border-radius: 8px;
  font-size: 10px;
  font-weight: 500;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
  max-width: 120px;
  text-align: center;
  word-break: break-word;
`;

const LoadingCard = styled.div`
  height: 280px;
  border-radius: 12px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: loading 1.5s infinite;

  @keyframes loading {
    0% {
      background-position: 200% 0;
    }
    100% {
      background-position: -200% 0;
    }
  }
`;

const EmptyState = styled.div`
  grid-column: 1 / -1;
  text-align: center;
  padding: 60px 20px;
  color: #888;
`;

const EmptyStateTitle = styled.h3`
  font-size: 20px;
  margin-bottom: 8px;
  color: #666;
`;

const EmptyStateText = styled.p`
  font-size: 16px;
  line-height: 1.5;
`;

// Helper functions
const formatDuration = (seconds: number | null): string => {
  if (!seconds) return 'Unknown';
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  if (minutes < 60) {
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  }
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  return `${hours}:${remainingMinutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
};

const formatViews = (views: number): string => {
  if (views >= 1000000) {
    return `${(views / 1000000).toFixed(1)}M`;
  }
  if (views >= 1000) {
    return `${(views / 1000).toFixed(1)}K`;
  }
  return views.toString();
};

const formatPublishedDate = (publishedAt: string): string => {
  const date = new Date(publishedAt);
  const now = new Date();
  const diffInDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));
  
  if (diffInDays === 0) return 'Today';
  if (diffInDays === 1) return 'Yesterday';
  if (diffInDays < 7) return `${diffInDays}d ago`;
  if (diffInDays < 30) return `${Math.floor(diffInDays / 7)}w ago`;
  if (diffInDays < 365) return `${Math.floor(diffInDays / 30)}mo ago`;
  return `${Math.floor(diffInDays / 365)}y ago`;
};

const MeditationResults: React.FC<MeditationResultsProps> = ({ 
  meditations, 
  loading = false, 
  onMeditationClick 
}) => {
  const handleCardClick = (e: React.MouseEvent<HTMLAnchorElement>, meditation: MeditationResultItem) => {
    if (onMeditationClick) {
      e.preventDefault(); // Prevent default link behavior when custom handler is provided
      onMeditationClick(meditation);
    }
    // If no custom handler, let the default link behavior happen
  };

  if (loading) {
    return (
      <Container>
        {Array.from({ length: 6 }).map((_, index) => (
          <LoadingCard key={index} />
        ))}
      </Container>
    );
  }

  if (meditations.length === 0) {
    return (
      <Container>
        <EmptyState>
          <EmptyStateTitle>No Meditations Found</EmptyStateTitle>
          <EmptyStateText>
            There are no published meditations to display at this time.
          </EmptyStateText>
        </EmptyState>
      </Container>
    );
  }

  return (
    <Container>
      {meditations.map((meditation) => {
        // Build S3 URL from art key
        let backgroundImage = undefined;
        if (meditation.art_file) {
          
            // New format - just the S3 key, build the full S3 URL
            backgroundImage = `https://${config.awsS3ArtSmall}.s3.${config.awsRegion}.amazonaws.com/${meditation.art_file}`;
          
        }

        return (
          <MeditationCard
            key={meditation.id}
            $backgroundImage={backgroundImage}
            href={`/meditation/${meditation.id}`}
            onClick={(e) => handleCardClick(e, meditation)}
          >
            <TypeBadgeContainer>
              <TypeBadge $isUnguided={meditation.is_unguided}>
                {meditation.is_unguided ? 'Unguided' : 'Guided'}
              </TypeBadge>
              {!meditation.is_unguided && meditation.voice_name && (
                <VoiceName>{meditation.voice_name}</VoiceName>
              )}
            </TypeBadgeContainer>
            
            <CardContent>
              <MeditationTitle>{meditation.script_name}</MeditationTitle>
              <MeditationDescription>{meditation.script_description}</MeditationDescription>
              
              <CardMeta>
                <AuthorInfo>
                  <ProfileLink to={`/profile/${meditation.username}`}>
                    {meditation.profile_image && (
                      <ProfileImage 
                        src={
                          `https://${config.awsS3Avatars}.s3.${config.awsRegion}.amazonaws.com/48/${meditation.profile_image}` // New S3 key format
                        } 
                        alt={meditation.username}
                      />
                    )}
                    <AuthorName>{meditation.username}</AuthorName>
                  </ProfileLink>
                </AuthorInfo>
                
                <MeditationStats>
                  <StatItem>
                    <Icon icon={FaRegClock} /> {formatDuration(meditation.final_duration)}
                  </StatItem>
                  <StatItem>
                    <Icon icon={FaEye} /> {formatViews(meditation.views)}
                  </StatItem>
                  <StatItem>
                    {formatPublishedDate(meditation.published_at)}
                  </StatItem>
                </MeditationStats>
              </CardMeta>
            </CardContent>
          </MeditationCard>
        );
      })}
    </Container>
  );
};

export default MeditationResults;